/**
 * BackorderRecipientsPanel
 * ------------------------
 * Per-backorder editable recipients list. Sits in the Details tab.
 *
 *   • Loads saved recipients[]  → editable table
 *   • If empty, offers "Auto-load suggestions" to populate from the
 *     requestor + hospital's CRM contacts (server-side derived)
 *   • Each row: Name · Email · Role pill (Primary / Cc) · Delete
 *   • Add row inline at the bottom
 *   • Save replaces the whole array (PUT /recipients)
 *
 * The list is honoured by the PO Acknowledgment email and (going forward)
 * any other transactional emails on this backorder.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Plus, Trash2, Star, Loader2, Save, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyRow = () => ({ id: null, name: "", email: "", role: "cc" });

export default function BackorderRecipientsPanel({ backorder, token, onRefresh }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [recipients, setRecipients] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [haveSaved, setHaveSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/backorders/${backorder.id}/recipients`,
        { headers },
      );
      setRecipients(r.data?.recipients || []);
      setSuggested(r.data?.suggested || []);
      setHaveSaved(!!r.data?.have_saved);
      setDirty(false);
    } catch (e) {
      toast.error("Could not load recipients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [backorder.id]);

  const applySuggestions = () => {
    if (suggested.length === 0) return;
    setRecipients(suggested.map((s) => ({ ...s, id: null })));
    setDirty(true);
    toast.success(`Loaded ${suggested.length} suggested recipient${suggested.length === 1 ? "" : "s"}.`);
  };

  const setRow = (idx, patch) => {
    setRecipients((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const togglePrimary = (idx) => {
    setRecipients((rows) =>
      rows.map((r, i) => (i === idx
        ? { ...r, role: r.role === "primary" ? "cc" : "primary" }
        : { ...r, role: i !== idx && r.role === "primary" ? "cc" : r.role }
      )),
    );
    setDirty(true);
  };

  const removeRow = (idx) => {
    setRecipients((rows) => rows.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addRow = () => {
    setRecipients((rows) => [...rows, emptyRow()]);
    setDirty(true);
  };

  const save = async () => {
    const cleaned = recipients
      .map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        name: (r.name || "").trim() || null,
        email: (r.email || "").trim().toLowerCase(),
        role: r.role === "primary" ? "primary" : "cc",
      }))
      .filter((r) => r.email);
    const bad = cleaned.find((r) => !r.email.includes("@"));
    if (bad) {
      toast.error(`"${bad.email}" is not a valid email.`);
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/backorders/${backorder.id}/recipients`,
        { recipients: cleaned },
        { headers },
      );
      setRecipients(r.data?.recipients || []);
      setHaveSaved(true);
      setDirty(false);
      toast.success(`Saved ${r.data?.count ?? cleaned.length} recipient${r.data?.count === 1 ? "" : "s"}.`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-xl border border-blue-200 bg-blue-50/40 p-4"
      data-testid="recipients-panel"
    >
      <header className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900 inline-flex items-center gap-1.5">
          <UsersRound className="w-3.5 h-3.5" /> Notification Recipients
        </h3>
        <span className="text-[10px] text-slate-500">
          Receives PO Acks &amp; shipping updates for this backorder
        </span>
      </header>

      {loading ? (
        <div className="text-sm text-slate-500 py-4 text-center">
          <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…
        </div>
      ) : (
        <>
          {/* Suggestions banner when nothing saved yet */}
          {!haveSaved && recipients.length === 0 && suggested.length > 0 && (
            <div className="mb-3 p-3 rounded-md border border-blue-300 bg-white flex items-center justify-between gap-2" data-testid="recipients-suggested-banner">
              <div className="text-xs text-slate-700">
                <strong>{suggested.length} suggested</strong> recipient{suggested.length === 1 ? "" : "s"} from the requestor + this hospital's CRM contacts.
              </div>
              <Button size="sm" variant="outline" onClick={applySuggestions} className="text-xs" data-testid="recipients-apply-suggested">
                Load suggestions
              </Button>
            </div>
          )}

          {recipients.length === 0 && (
            <p className="text-xs text-slate-500 italic mb-2" data-testid="recipients-empty">
              No recipients set. PO &amp; shipping emails will fall back to the original requestor's address.
            </p>
          )}

          {recipients.map((r, idx) => (
            <div
              key={r.id || idx}
              className="grid grid-cols-12 gap-2 items-center bg-white border border-slate-200 rounded p-2 mb-1.5"
              data-testid={`recipient-row-${idx}`}
            >
              <Input
                value={r.name || ""}
                onChange={(e) => setRow(idx, { name: e.target.value })}
                placeholder="Name (optional)"
                className="col-span-3 h-8 text-sm"
                data-testid={`recipient-name-${idx}`}
              />
              <Input
                value={r.email || ""}
                onChange={(e) => setRow(idx, { email: e.target.value })}
                placeholder="email@hospital.org"
                type="email"
                className="col-span-6 h-8 text-sm"
                data-testid={`recipient-email-${idx}`}
              />
              <button
                type="button"
                onClick={() => togglePrimary(idx)}
                className={`col-span-2 h-8 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-1 transition-colors ${
                  r.role === "primary"
                    ? "bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200"
                    : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                }`}
                title={r.role === "primary" ? "Currently the primary (To) recipient — click to demote to Cc" : "Click to mark as the primary (To) recipient"}
                data-testid={`recipient-role-${idx}`}
              >
                <Star className={`w-3 h-3 ${r.role === "primary" ? "fill-amber-500 text-amber-500" : ""}`} />
                {r.role === "primary" ? "Primary" : "Cc"}
              </button>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="col-span-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded p-1 inline-flex justify-center"
                title="Remove recipient"
                data-testid={`recipient-remove-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <div className="flex items-center justify-between mt-2">
            <button
              type="button"
              onClick={addRow}
              className="text-xs font-bold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
              data-testid="recipient-add-btn"
            >
              <Plus className="w-3 h-3" /> Add recipient
            </button>
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-xs"
              data-testid="recipients-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {dirty ? "Save recipients" : "Saved"}
            </Button>
          </div>

          {haveSaved && recipients.some((r) => r.role === "primary") === false && recipients.length > 0 && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-flex items-center gap-1" data-testid="recipients-no-primary-warning">
              <Mail className="w-3 h-3" /> No primary set — emails will use the original requestor as the To address.
            </p>
          )}
        </>
      )}
    </section>
  );
}
